//mushroom-home-page.tsx

import React, {
  useState,
  Fragment,
  useContext,
  useRef,
  useEffect,
} from "react";
import { MushroomReadOnlyContext } from "../mushroom/review/mushroom-readonly-context";
import { clearMushroomGuestSession } from "../mushroom/guest/mushroom-guest-session";

import {
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Box,
  Alert,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AccountCircle from "@mui/icons-material/AccountCircle";
import HelpIcon from "@mui/icons-material/Help";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuIcon from "@mui/icons-material/Menu";
import CategoryIcon from "@mui/icons-material/Category";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ScienceIcon from "@mui/icons-material/Science";
import HomeIcon from "@mui/icons-material/Home";
import BuildIcon from "@mui/icons-material/Build";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { RiceBowl } from "@mui/icons-material";
import SchemaIcon from "@mui/icons-material/Schema";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { Route, Switch, Redirect, Link as RouterLink } from "react-router-dom";

import { ListItemLink } from "../components/list-items";
import { ErrorContext, UserContext } from "../components/app-routes";
import LoadingBackdrop from "../components/loading-backdrop";
import SetTimeZone from "../components/set-time-zone";

import logo from "../assets/mycologo.png";

import mixpanel from "mixpanel-browser";

import { growersApi } from "../services/api/growers";
import { Grower } from "../models/grower";

import SettingsPage from "./settings-page";
import SupportPage from "./support-page";
import DynamicSupportCategoryPage from "./dynamic-support-category-page";
import DynamicSupportArticlePage from "./dynamic-support-article-page";
import MushroomRoomsPage from "./mushroom-rooms-page";
import MushroomStrainsPage from "./mushroom-strains-page";
import MushroomRecipesPage from "./mushroom-recipes-page";
import MushroomMaterialsPage from "./mushroom-materials-page";
import MushroomInoculationsPage from "./mushroom-inoculations-page";
import MushroomWorkflowPage from "./mushroom-workflow-page";
import MushroomBoardPage from "./mushroom-board-page";
import MushroomTasksPage from "./mushroom-tasks-page";
import MushroomCustomersPage from "./mushroom-customers-page";
import MushroomProductsPage from "./mushroom-products-page";
import MushroomOrdersPage from "./mushroom-orders-page";
import MushroomProductionUnitsPage from "./mushroom-production-units-page";
import MushroomStrainEditPage from "./mushroom-strain-edit-page";

const drawerWidth = 240;
const mushroomTheme = createTheme({
  palette: {
    primary: {
      main: "#4981c1",
    },
  },
});

export default function MushroomHomePage(props: {
  onLogOut: () => void;
  onGrowerUpdated: () => void;
}) {
  const context = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] =
    useState<Element | null>(null);

  const submenuRef = useRef<HTMLDivElement | null>(null);
  const { readOnly } = useContext(MushroomReadOnlyContext);

  const [setupAnchorEl, setSetupAnchorEl] = useState<null | HTMLElement>(null);
  const handleToggleSetup = (e: React.MouseEvent<HTMLDivElement>) => {
    if (setupAnchorEl) {
      setSetupAnchorEl(null);
    } else {
      setSetupAnchorEl(e.currentTarget);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        setupAnchorEl &&
        submenuRef.current &&
        !submenuRef.current.contains(event.target as Node) &&
        !setupAnchorEl.contains(event.target as Node)
      ) {
        setSetupAnchorEl(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setupAnchorEl]);

  const grower = context.grower;
  if (!grower) throw new Error("Invalid grower");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const handleSetTimeZoneOnClickOk = async (timeZone: string) => {
    setBusy(true);
    try {
      await growersApi.patch({ timeZone });
      props.onGrowerUpdated();
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleClickLink = () => setMobileOpen(false);

  const handleClickProfileMenu: React.MouseEventHandler<HTMLButtonElement> = (
    e,
  ) => setProfileMenuAnchorEl(e.currentTarget);
  const handleCloseProfileMenu = () => setProfileMenuAnchorEl(null);

  const handleClickLogOut = () => {
    setProfileMenuAnchorEl(null);
    props.onLogOut();
  };

  const openProfileMenu = Boolean(profileMenuAnchorEl);

  const DrawerListItemLinks = ({ grower }: { grower: Grower }) => (
    <Fragment>
      <Box sx={{ p: 1, pt: 3.2 }}>
        <img alt="logo" src={logo} width="215" />
      </Box>

      <List>
        <ListItemLink
          to="/mushroom/home/board"
          primary="Production Board"
          icon={<ListAltIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/mushroom/home/tasks"
          primary="Tasks"
          icon={<ScheduleIcon />}
          onClick={handleClickLink}
        />

        <ListItemLink
          to="/mushroom/home/inoculations"
          primary="Inoculations"
          icon={<ScienceIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/mushroom/home/customers"
          primary="Customers"
          icon={<PeopleOutlineIcon />}
          onClick={handleClickLink}
        />

        <ListItemLink
          to="/mushroom/home/products"
          primary="Products"
          icon={<Inventory2OutlinedIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/mushroom/home/orders"
          primary="Orders"
          icon={<ReceiptLongIcon />}
          onClick={handleClickLink}
        />
        {/* SETUP SECTION */}
        <ListItemButton
          onClick={handleToggleSetup}
          sx={{
            "& .MuiListItemIcon-root": {
              color: "black",
            },
          }}
        >
          <ListItemIcon>
            <BuildIcon />
          </ListItemIcon>
          <ListItemText primary="Setup" />
          <ListItemIcon sx={{ minWidth: 0, ml: "auto" }}>
            <ChevronRightIcon />
          </ListItemIcon>
        </ListItemButton>
        {isMobile
          ? setupAnchorEl && (
              <List component="div" disablePadding>
                <ListItemLink
                  to="/mushroom/home/rooms"
                  primary="Rooms"
                  icon={<MeetingRoomIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/mushroom/home/production-units"
                  primary="Production Units"
                  icon={<Inventory2OutlinedIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />

                <ListItemLink
                  to="/mushroom/home/strains"
                  primary="Strains"
                  icon={<CategoryIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/mushroom/home/recipes"
                  primary="Recipes"
                  icon={<MenuBookIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/mushroom/home/materials"
                  primary="Materials"
                  icon={<CategoryIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />
                <ListItemLink
                  to="/mushroom/home/workflow"
                  primary="Workflow"
                  icon={<SchemaIcon />}
                  onClick={() => {
                    handleClickLink();
                    setSetupAnchorEl(null);
                  }}
                  nested
                />
              </List>
            )
          : setupAnchorEl && (
              <Box
                ref={submenuRef}
                sx={{
                  position: "fixed",
                  top: setupAnchorEl.getBoundingClientRect().top,
                  left: setupAnchorEl.getBoundingClientRect().right + 8,
                  backgroundColor: "#fcfcfce0",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  boxShadow: "0px 2px 6px rgba(0,0,0,.75)",
                  zIndex: 2000,
                  width: 250,
                  p: 0.5,
                }}
              >
                <List>
                  <ListItemLink
                    to="/mushroom/home/rooms"
                    primary="Rooms"
                    icon={<MeetingRoomIcon />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to="/mushroom/home/production-units"
                    primary="Production Units"
                    icon={<Inventory2OutlinedIcon />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />

                  <ListItemLink
                    to="/mushroom/home/strains"
                    primary="Strains"
                    icon={<RiceBowl />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to="/mushroom/home/recipes"
                    primary="Recipes"
                    icon={<MenuBookIcon />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to="/mushroom/home/materials"
                    primary="Materials"
                    icon={<CategoryIcon />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />
                  <ListItemLink
                    to="/mushroom/home/workflow"
                    primary="Workflow"
                    icon={<SchemaIcon />}
                    onClick={() => {
                      handleClickLink();
                      setSetupAnchorEl(null);
                    }}
                  />
                </List>
              </Box>
            )}

        <Divider />

        <ListItemLink
          to="/home"
          primary="Back to SeedLeaf"
          icon={<HomeIcon />}
          onClick={handleClickLink}
        />

        <ListItemLink
          to="/mushroom/home/support"
          primary="Support"
          icon={<HelpIcon />}
          onClick={handleClickLink}
        />
        <ListItemLink
          to="/mushroom/home/settings"
          primary="Settings"
          icon={<SettingsIcon />}
          onClick={handleClickLink}
        />
      </List>
    </Fragment>
  );

  return (
    <ThemeProvider theme={mushroomTheme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
          }}
        >
          {context.user.EmailVerified === false && (
            <Box
              sx={{
                display: "block",
                backgroundColor: theme.palette.primary.light,
                textAlign: "center",
                color: theme.palette.primary.contrastText,
                p: 1,
              }}
            >
              <Button
                component={RouterLink}
                to="/verify-email"
                size="small"
                variant="contained"
                color="primary"
                sx={{ color: "white" }}
              >
                Please verify your email
              </Button>
            </Box>
          )}
          <Toolbar sx={{ justifyContent: "end" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
              size="large"
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              size="medium"
              onClick={handleClickProfileMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={profileMenuAnchorEl}
              open={openProfileMenu}
              onClose={handleCloseProfileMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <MenuItem onClick={handleClickLogOut}>
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                <ListItemText primary="Log out" />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Drawer Navigation */}
        <Box
          component="nav"
          sx={{
            width: { sm: drawerWidth },
            flexShrink: { sm: 0 },
          }}
        >
          {isMobile && (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: "block", sm: "none" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                },
              }}
            >
              <DrawerListItemLinks grower={grower} />
            </Drawer>
          )}
          {isDesktop && (
            <Drawer
              variant="permanent"
              open
              sx={{
                display: { xs: "none", sm: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                },
              }}
            >
              <DrawerListItemLinks grower={grower} />
            </Drawer>
          )}
        </Box>

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          {readOnly && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Alert
                severity="warning"
                action={
                  <span
                    style={{ cursor: "pointer", fontWeight: 600 }}
                    onClick={() => {
                      clearMushroomGuestSession();
                      window.location.href = "/mushroom/guest";
                    }}
                  >
                    Exit guest
                  </span>
                }
              >
                STAGING – GUEST MODE (READ-ONLY)
              </Alert>
            </Box>
          )}
          <Switch>
            <Route
              exact
              path="/mushroom/home/board"
              component={MushroomBoardPage}
            />
            <Route
              exact
              path="/mushroom/home/tasks"
              component={MushroomTasksPage}
            />
            <Route
              exact
              path="/mushroom/home/customers"
              component={MushroomCustomersPage}
            />
            <Route
              exact
              path="/mushroom/home/products"
              component={MushroomProductsPage}
            />
            <Route
              exact
              path="/mushroom/home/orders"
              component={MushroomOrdersPage}
            />
            <Route
              exact
              path="/mushroom/home/inoculations"
              component={MushroomInoculationsPage}
            />
            <Route
              exact
              path="/mushroom/home/production-units"
              component={MushroomProductionUnitsPage}
            />
            <Route
              exact
              path="/mushroom/home/strains/new"
              component={MushroomStrainEditPage}
            />
            <Route
              exact
              path="/mushroom/home/strains/:id"
              component={MushroomStrainEditPage}
            />
            <Route
              exact
              path="/mushroom/home/strains"
              component={MushroomStrainsPage}
            />

            <Route
              exact
              path="/mushroom/home/recipes"
              component={MushroomRecipesPage}
            />
            <Route
              exact
              path="/mushroom/home/materials"
              component={MushroomMaterialsPage}
            />
            <Route
              exact
              path="/mushroom/home/workflow"
              component={MushroomWorkflowPage}
            />
            <Route
              exact
              path="/mushroom/home/rooms"
              component={MushroomRoomsPage}
            />
            <Route path="/mushroom/home/settings">
              <SettingsPage onGrowerUpdated={props.onGrowerUpdated} />
            </Route>

            <Route
              exact
              path="/mushroom/home/support"
              component={SupportPage}
            />
            <Route
              exact
              path="/mushroom/home/support/:category"
              component={DynamicSupportCategoryPage}
            />
            <Route
              exact
              path="/mushroom/home/support/:category/:article"
              component={DynamicSupportArticlePage}
            />

            <Route path="/mushroom/home">
              <Redirect to="/mushroom/home/board" />
            </Route>
          </Switch>
        </Box>

        <LoadingBackdrop open={busy} />
        <SetTimeZone
          open={!context.grower?.TimeZone}
          onClickOk={handleSetTimeZoneOnClickOk}
        />
      </Box>
    </ThemeProvider>
  );
}
